import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CreditCard,
  QrCode,
  ReceiptText,
  Lock,
  ShieldCheck,
  Truck,
  Loader2,
  Copy,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  ExternalLink,
  ShoppingBag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { formatBRL } from "@/lib/format";

const UF_LIST = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG",
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

const maskCPF = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  let out = d.slice(0, 3);
  if (d.length > 3) out += "." + d.slice(3, 6);
  if (d.length > 6) out += "." + d.slice(6, 9);
  if (d.length > 9) out += "-" + d.slice(9, 11);
  return out;
};

const maskPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const maskCEP = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

const maskCardNumber = (v) =>
  v.replace(/\D/g, "").slice(0, 19).replace(/(\d{4})(?=\d)/g, "$1 ").trim();

const maskExpiry = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

const INITIAL_CUSTOMER = {
  name: "",
  email: "",
  phone: "",
  cpf: "",
  address: {
    zip_code: "",
    street_name: "",
    street_number: "",
    complement: "",
    neighborhood: "",
    city: "",
    federal_unit: "",
  },
};

function Field({ label, error, htmlFor, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-wide text-mute">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

function InputBox({ id, value, onChange, placeholder, invalid, ...rest }) {
  return (
    <Input
      id={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`h-11 bg-elevated border-white/10 text-white placeholder:text-mute/60 focus-visible:ring-cyan/60 ${
        invalid ? "border-red-400/70" : ""
      }`}
      {...rest}
    />
  );
}

export default function Checkout() {
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState(INITIAL_CUSTOMER);
  const [errors, setErrors] = useState({});
  const [method, setMethod] = useState("card");
  const [card, setCard] = useState({ number: "", holder: "", expiry: "", cvv: "" });
  const [installments, setInstallments] = useState(1);
  const [paying, setPaying] = useState(false);
  const [pixResult, setPixResult] = useState(null);
  const [boletoUrl, setBoletoUrl] = useState("");
  const [paymentId, setPaymentId] = useState("");
  const [copied, setCopied] = useState(false);
  const [payError, setPayError] = useState("");
  const [plans, setPlans] = useState([]);
  const [planLoading, setPlanLoading] = useState(false);

  const addr = customer.address;
  const setAddr = (k, v) => setCustomer((c) => ({ ...c, address: { ...c.address, [k]: v } }));

  // Parcelas reais do Mercado Pago (com juros configurados pelo vendedor), por BIN do cartão
  useEffect(() => {
    const digits = card.number.replace(/\D/g, "");
    const bin = digits.length >= 6 ? digits.slice(0, 6) : "";
    if (method !== "card" || !bin || total <= 0) {
      setPlans([]);
      setPlanLoading(false);
      return;
    }
    let stop = false;
    setPlanLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/mp/installments", { params: { bin, amount: total } });
        if (stop) return;
        const rows = [];
        const seen = new Set();
        (Array.isArray(data) ? data : []).forEach((g) =>
          (g.payer_costs || []).forEach((p) => {
            if (p.installments && !seen.has(p.installments)) {
              seen.add(p.installments);
              rows.push(p);
            }
          })
        );
        rows.sort((a, b) => a.installments - b.installments);
        setPlans(rows);
        setInstallments((prev) =>
          rows.some((r) => r.installments === prev) ? prev : rows[0]?.installments || 1
        );
      } catch {
        if (!stop) setPlans([]);
      } finally {
        if (!stop) setPlanLoading(false);
      }
    }, 450);
    return () => {
      stop = true;
      clearTimeout(t);
    };
  }, [card.number, total, method]);

  const validateCustomer = () => {
    const e = {};
    if (customer.name.trim().split(/\s+/).length < 2) e.name = "Informe seu nome completo";
    if (!/^\S+@\S+\.\S+$/.test(customer.email.trim())) e.email = "E-mail inválido";
    if (customer.phone.replace(/\D/g, "").length < 10) e.phone = "Telefone inválido";
    if (customer.cpf.replace(/\D/g, "").length !== 11) e.cpf = "CPF inválido";
    if (addr.zip_code.replace(/\D/g, "").length !== 8) e.zip_code = "CEP inválido";
    if (!addr.street_name.trim()) e.street_name = "Informe a rua";
    if (!addr.street_number.trim()) e.street_number = "Informe o número";
    if (!addr.neighborhood.trim()) e.neighborhood = "Informe o bairro";
    if (!addr.city.trim()) e.city = "Informe a cidade";
    if (addr.federal_unit.trim().length !== 2) e.federal_unit = "UF inválida";
    setErrors(e);
    if (Object.keys(e).length > 0) toast.error("Confira os campos destacados.");
    return Object.keys(e).length === 0;
  };

  const goPayment = () => {
    if (validateCustomer()) setStep(2);
  };

  const selectedPlan = plans.find((p) => p.installments === installments);
  const payTotal = selectedPlan ? selectedPlan.total_amount : total;

  const validateCard = () => {
    const e = {};
    if (card.number.replace(/\D/g, "").length < 13) e.card_number = "Número incompleto";
    if (card.holder.trim().length < 3) e.card_holder = "Nome impresso no cartão";
    if (card.expiry.replace(/\D/g, "").length !== 4) e.card_expiry = "Validade MM/AA";
    if (card.cvv.length < 3) e.card_cvv = "CVV";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => ({
    customer,
    items: items.map((i) => ({ product_id: i.id, quantity: i.qty, color: i.color || "" })),
    origin_url: window.location.origin,
  });

  const friendlyPayError = (detail) => {
    const d = String(detail);
    if (/live credentials/i.test(d))
      return "O pagamento direto no site ainda não está liberado na conta Mercado Pago da loja. Use a opção de pagamento via checkout do Mercado Pago abaixo.";
    if (/bin_not_found|invalid_card|card_invalid|invalid.*card|card.*invalid/i.test(d))
      return "Não foi possível validar este cartão. Confira o número digitado ou tente outro cartão.";
    return d;
  };

  const pay = async () => {
    if (items.length === 0) return;
    if (method === "card" && !validateCard()) return;
    setPaying(true);
    setPayError("");
    try {
      const payload = buildPayload();
      payload.installments = method === "card" ? installments : 1;
      if (method === "card") {
        const [mm, yy] = card.expiry.split("/");
        payload.card = {
          number: card.number,
          holder: card.holder,
          exp_month: Number(mm),
          exp_year: 2000 + Number(yy),
          cvv: card.cvv,
        };
      } else {
        payload.payment_method_id = method === "pix" ? "pix" : "bolbradesco";
      }
      const { data } = await api.post("/checkout/mp/direct", payload);
      if (method === "card") {
        clear();
        navigate(`/payment/success?provider=mp&payment_id=${data.payment_id}`);
        return;
      }
      setPaymentId(data.payment_id);
      if (method === "pix") setPixResult(data.pix || {});
      if (method === "boleto") setBoletoUrl(data.boleto_url || "");
    } catch (err) {
      const msg = friendlyPayError(
        err.response?.data?.detail || "Não foi possível processar o pagamento. Tente novamente."
      );
      setPayError(msg);
      toast.error(msg);
    } finally {
      setPaying(false);
    }
  };

  const checkoutViaMercadoPago = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/checkout/mp", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty, color: i.color || "" })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível iniciar o checkout.");
      setPaying(false);
    }
  };

  const copyPix = async () => {
    try {
      await navigator.clipboard.writeText(pixResult.qr_code || "");
      setCopied(true);
      toast.success("Código Pix copiado!");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error("Não foi possível copiar. Selecione o código manualmente.");
    }
  };

  if (items.length === 0 && !pixResult && !boletoUrl) {
    return (
      <div className="bg-ink min-h-[70vh] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <ShoppingBag size={44} strokeWidth={1.2} className="text-mute" />
        <h1 className="font-display text-3xl">Seu carrinho está vazio</h1>
        <p className="text-mute">Adicione produtos para finalizar a compra.</p>
        <Button
          className="h-12 bg-cyan text-ink font-semibold hover:bg-cyan-dark"
          onClick={() => navigate("/loja")}
          data-testid="checkout-empty-explore-button"
        >
          Explorar catálogo
        </Button>
      </div>
    );
  }

  if (pixResult) {
    return (
      <div className="bg-ink min-h-[80vh] px-4 sm:px-6 py-12">
        <div className="max-w-xl mx-auto bg-surface border border-white/10 rounded-lg p-6 sm:p-10 text-center space-y-6" data-testid="pix-payment-screen">
          <div className="flex justify-center">
            <CheckCircle2 size={48} className="text-cyan" strokeWidth={1.4} />
          </div>
          <div>
            <h1 className="font-display text-3xl">Escaneie o QR Code Pix</h1>
            <p className="text-mute mt-2 text-sm">
              Valor: <span className="text-cyan font-semibold">{formatBRL(total)}</span> • A confirmação é automática após o pagamento.
            </p>
          </div>
          {pixResult.qr_code_base64 && (
            <img
              src={`data:image/png;base64,${pixResult.qr_code_base64}`}
              alt="QR Code Pix"
              className="mx-auto w-56 h-56 rounded-md bg-white p-2"
              data-testid="pix-qr-image"
            />
          )}
          {pixResult.qr_code && (
            <div className="space-y-2 text-left">
              <p className="text-xs uppercase tracking-wide text-mute">Pix copia e cola</p>
              <div className="bg-elevated border border-white/10 rounded p-3 text-xs break-all text-mute" data-testid="pix-code">
                {pixResult.qr_code}
              </div>
              <Button
                variant="outline"
                className="w-full border-cyan/50 text-cyan hover:bg-cyan/10"
                onClick={copyPix}
                data-testid="pix-copy-code-button"
              >
                {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                {copied ? "Copiado!" : "Copiar código Pix"}
              </Button>
            </div>
          )}
          {pixResult.ticket_url && (
            <a
              href={pixResult.ticket_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 text-sm text-cyan underline underline-offset-4"
              data-testid="pix-open-url"
            >
              Abrir no app do banco <ExternalLink size={14} />
            </a>
          )}
          <Button
            className="w-full h-12 bg-cyan text-ink font-semibold hover:bg-cyan-dark"
            onClick={() => {
              clear();
              navigate(`/payment/success?provider=mp&payment_id=${paymentId}`);
            }}
            data-testid="pix-check-status-button"
          >
            Já fiz o pagamento — acompanhar pedido
          </Button>
        </div>
      </div>
    );
  }

  if (boletoUrl) {
    return (
      <div className="bg-ink min-h-[80vh] px-4 sm:px-6 py-12">
        <div className="max-w-xl mx-auto bg-surface border border-white/10 rounded-lg p-6 sm:p-10 text-center space-y-6" data-testid="boleto-payment-screen">
          <div className="flex justify-center">
            <ReceiptText size={48} className="text-cyan" strokeWidth={1.4} />
          </div>
          <div>
            <h1 className="font-display text-3xl">Boleto gerado!</h1>
            <p className="text-mute mt-2 text-sm">
              Valor: <span className="text-cyan font-semibold">{formatBRL(total)}</span> • O pedido é liberado após a confirmação do banco (1 a 2 dias úteis).
            </p>
          </div>
          <a
            href={boletoUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-12 items-center rounded-md bg-cyan px-7 font-semibold text-ink transition-all hover:bg-cyan-dark"
            data-testid="boleto-open-url"
          >
            Visualizar boleto <ExternalLink size={16} className="ml-2" />
          </a>
          <Button
            variant="outline"
            className="w-full h-12 border-white/20"
            onClick={() => {
              clear();
              navigate(`/payment/success?provider=mp&payment_id=${paymentId}`);
            }}
            data-testid="boleto-check-status-button"
          >
            Acompanhar pedido
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ink min-h-screen px-4 sm:px-6 py-10 sm:py-14">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-3xl sm:text-4xl">Finalizar compra</h1>
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest" data-testid="checkout-steps">
            <span className={step === 1 ? "text-cyan" : "text-mute"}>1 — Dados</span>
            <span className="text-mute">/</span>
            <span className={step === 2 ? "text-cyan" : "text-mute"}>2 — Pagamento</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-8 mt-8">
          <div className="space-y-6">
            {step === 1 && (
              <div className="bg-surface border border-white/10 rounded-lg p-6 sm:p-8" data-testid="checkout-customer-form">
                <h2 className="font-display text-xl flex items-center gap-2">
                  <ShieldCheck size={18} className="text-cyan" /> Seus dados
                </h2>
                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                  <div className="sm:col-span-2">
                    <Field label="Nome completo" error={errors.name} htmlFor="c-name">
                      <InputBox id="c-name" value={customer.name} invalid={!!errors.name}
                        onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
                        placeholder="Ex.: Maria Souza" data-testid="checkout-form-name" />
                    </Field>
                  </div>
                  <Field label="E-mail" error={errors.email} htmlFor="c-email">
                    <InputBox id="c-email" type="email" value={customer.email} invalid={!!errors.email}
                      onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))}
                      placeholder="voce@email.com" data-testid="checkout-form-email" />
                  </Field>
                  <Field label="Telefone / WhatsApp" error={errors.phone} htmlFor="c-phone">
                    <InputBox id="c-phone" value={customer.phone} invalid={!!errors.phone}
                      onChange={(e) => setCustomer((c) => ({ ...c, phone: maskPhone(e.target.value) }))}
                      placeholder="(11) 91234-5678" inputMode="tel" data-testid="checkout-form-phone" />
                  </Field>
                  <Field label="CPF" error={errors.cpf} htmlFor="c-cpf">
                    <InputBox id="c-cpf" value={customer.cpf} invalid={!!errors.cpf}
                      onChange={(e) => setCustomer((c) => ({ ...c, cpf: maskCPF(e.target.value) }))}
                      placeholder="000.000.000-00" inputMode="numeric" data-testid="checkout-form-cpf" />
                  </Field>
                  <Field label="CEP" error={errors.zip_code} htmlFor="c-cep">
                    <InputBox id="c-cep" value={addr.zip_code} invalid={!!errors.zip_code}
                      onChange={(e) => setAddr("zip_code", maskCEP(e.target.value))}
                      placeholder="00000-000" inputMode="numeric" data-testid="checkout-form-cep" />
                  </Field>
                  <Field label="Rua / Avenida" error={errors.street_name} htmlFor="c-street">
                    <InputBox id="c-street" value={addr.street_name} invalid={!!errors.street_name}
                      onChange={(e) => setAddr("street_name", e.target.value)}
                      placeholder="Ex.: Av. Paulista" data-testid="checkout-form-street" />
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Número" error={errors.street_number} htmlFor="c-number">
                      <InputBox id="c-number" value={addr.street_number} invalid={!!errors.street_number}
                        onChange={(e) => setAddr("street_number", e.target.value.slice(0, 10))}
                        placeholder="123" inputMode="numeric" data-testid="checkout-form-number" />
                    </Field>
                    <Field label="Complemento" htmlFor="c-complement">
                      <InputBox id="c-complement" value={addr.complement}
                        onChange={(e) => setAddr("complement", e.target.value.slice(0, 60))}
                        placeholder="Opcional" data-testid="checkout-form-complement" />
                    </Field>
                  </div>
                  <Field label="Bairro" error={errors.neighborhood} htmlFor="c-neighborhood">
                    <InputBox id="c-neighborhood" value={addr.neighborhood} invalid={!!errors.neighborhood}
                      onChange={(e) => setAddr("neighborhood", e.target.value)}
                      placeholder="Ex.: Centro" data-testid="checkout-form-neighborhood" />
                  </Field>
                  <div className="grid grid-cols-[1fr_90px] gap-4">
                    <Field label="Cidade" error={errors.city} htmlFor="c-city">
                      <InputBox id="c-city" value={addr.city} invalid={!!errors.city}
                        onChange={(e) => setAddr("city", e.target.value)}
                        placeholder="Ex.: São Paulo" data-testid="checkout-form-city" />
                    </Field>
                    <Field label="UF" error={errors.federal_unit} htmlFor="c-uf">
                      <InputBox id="c-uf" value={addr.federal_unit} invalid={!!errors.federal_unit}
                        onChange={(e) => setAddr("federal_unit", e.target.value.toUpperCase().slice(0, 2).replace(/[^A-Z]/g, ""))}
                        placeholder="SP" data-testid="checkout-form-uf" />
                    </Field>
                  </div>
                </div>
                <Button
                  onClick={goPayment}
                  className="w-full h-12 mt-8 bg-cyan text-ink font-semibold hover:bg-cyan-dark"
                  data-testid="checkout-continue-button"
                >
                  Continuar para o pagamento
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="bg-surface border border-white/10 rounded-lg p-6 sm:p-8" data-testid="checkout-payment-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="font-display text-xl">Forma de pagamento</h2>
                  <button
                    onClick={() => setStep(1)}
                    className="text-xs text-mute hover:text-cyan flex items-center gap-1 transition-colors"
                    data-testid="checkout-edit-data-button"
                  >
                    <ArrowLeft size={13} /> Editar meus dados
                  </button>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 mt-6">
                  {[
                    { id: "card", label: "Cartão de crédito", icon: CreditCard, hint: "Parcelado ou à vista" },
                    { id: "pix", label: "Pix", icon: QrCode, hint: "Aprovação na hora" },
                    { id: "boleto", label: "Boleto", icon: ReceiptText, hint: "1-2 dias úteis" },
                  ].map((m) => {
                    const Icon = m.icon;
                    const active = method === m.id;
                    return (
                      <button
                        key={m.id}
                        onClick={() => setMethod(m.id)}
                        className={`rounded-md border p-4 text-left transition-all ${
                          active
                            ? "border-cyan bg-cyan/10"
                            : "border-white/10 bg-elevated/60 hover:border-white/25"
                        }`}
                        data-testid={`checkout-method-${m.id}`}
                      >
                        <Icon size={20} className={active ? "text-cyan" : "text-mute"} />
                        <p className={`text-sm font-medium mt-2 ${active ? "text-cyan" : "text-white"}`}>{m.label}</p>
                        <p className="text-xs text-mute mt-0.5">{m.hint}</p>
                      </button>
                    );
                  })}
                </div>

                {method === "card" && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="card-form">
                    <div className="sm:col-span-2">
                      <Field label="Número do cartão" error={errors.card_number} htmlFor="card-number">
                        <InputBox id="card-number" value={card.number} invalid={!!errors.card_number}
                          onChange={(e) => setCard((c) => ({ ...c, number: maskCardNumber(e.target.value) }))}
                          placeholder="0000 0000 0000 0000" inputMode="numeric" autoComplete="cc-number"
                          data-testid="card-form-number" />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Nome impresso no cartão" error={errors.card_holder} htmlFor="card-holder">
                        <InputBox id="card-holder" value={card.holder} invalid={!!errors.card_holder}
                          onChange={(e) => setCard((c) => ({ ...c, holder: e.target.value.toUpperCase().slice(0, 60) }))}
                          placeholder="Como está no cartão" autoComplete="cc-name" data-testid="card-form-holder" />
                      </Field>
                    </div>
                    <Field label="Validade" error={errors.card_expiry} htmlFor="card-expiry">
                      <InputBox id="card-expiry" value={card.expiry} invalid={!!errors.card_expiry}
                        onChange={(e) => setCard((c) => ({ ...c, expiry: maskExpiry(e.target.value) }))}
                        placeholder="MM/AA" inputMode="numeric" data-testid="card-form-expiry" />
                    </Field>
                    <Field label="CVV" error={errors.card_cvv} htmlFor="card-cvv">
                      <InputBox id="card-cvv" value={card.cvv} invalid={!!errors.card_cvv}
                        onChange={(e) => setCard((c) => ({ ...c, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                        placeholder="123" inputMode="numeric" type="password" data-testid="card-form-cvv" />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Parcelamento" htmlFor="installments">
                        <select
                          id="installments"
                          value={installments}
                          onChange={(e) => setInstallments(Number(e.target.value))}
                          className="h-11 w-full rounded-md bg-elevated border border-white/10 text-white text-sm px-3 focus:outline-none focus:ring-2 focus:ring-cyan/60"
                          data-testid="checkout-installments-select"
                        >
                          {planLoading ? (
                            <option value={installments}>Calculando parcelas no Mercado Pago…</option>
                          ) : plans.length > 0 ? (
                            plans.map((p) => {
                              const comJuros = p.total_amount > total + 0.01;
                              return (
                                <option key={p.installments} value={p.installments}>
                                  {p.installments}x de {formatBRL(p.installment_amount)}
                                  {comJuros ? ` — total ${formatBRL(p.total_amount)}` : " sem juros"}
                                </option>
                              );
                            })
                          ) : (
                            Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                              <option key={n} value={n} disabled={total / n < 5}>
                                {n}x de {formatBRL(total / n)}
                              </option>
                            ))
                          )}
                        </select>
                      </Field>
                      {selectedPlan && selectedPlan.total_amount > total + 0.01 && (
                        <p className="text-xs text-mute mt-2" data-testid="installments-total-note">
                          Total a pagar com juros do Mercado Pago: {formatBRL(selectedPlan.total_amount)}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {method === "pix" && (
                  <p className="text-sm text-mute mt-6 flex items-start gap-2" data-testid="pix-hint">
                    <QrCode size={16} className="text-cyan shrink-0 mt-0.5" />
                    Ao confirmar, geramos o QR Code para você pagar pelo app do seu banco. A aprovação é quase instantânea.
                  </p>
                )}
                {method === "boleto" && (
                  <p className="text-sm text-mute mt-6 flex items-start gap-2" data-testid="boleto-hint">
                    <ReceiptText size={16} className="text-cyan shrink-0 mt-0.5" />
                    Ao confirmar, geramos o boleto com seu endereço. O pedido é enviado após a confirmação do pagamento.
                  </p>
                )}

                {payError && (
                  <div
                    className="mt-6 rounded-md border border-red-400/40 bg-red-400/10 p-3 text-sm text-red-300 flex items-start gap-2"
                    data-testid="checkout-pay-error"
                  >
                    <XCircle size={16} className="mt-0.5 shrink-0" />
                    {payError}
                  </div>
                )}

                <Button
                  onClick={pay}
                  disabled={paying}
                  className="w-full h-12 mt-6 bg-cyan text-ink font-semibold hover:bg-cyan-dark"
                  data-testid="checkout-pay-button"
                >
                  {paying ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : method === "card" ? (
                    `Pagar ${formatBRL(payTotal)} em ${installments}x`
                  ) : (
                    `Gerar ${method === "pix" ? "Pix" : "boleto"} de ${formatBRL(total)}`
                  )}
                </Button>

                <button
                  onClick={checkoutViaMercadoPago}
                  disabled={paying}
                  className="w-full text-center text-xs text-mute hover:text-cyan underline underline-offset-4 mt-4 transition-colors"
                  data-testid="checkout-mercado-pago-redirect"
                >
                  Prefere pagar direto no Mercado Pago? Continue no checkout do Mercado Pago
                </button>

                <p className="text-xs text-mute flex items-center justify-center gap-2 mt-4">
                  <Lock size={12} className="text-cyan" />
                  Pagamento processado com segurança pelo Mercado Pago.
                </p>
              </div>
            )}
          </div>

          <aside className="lg:sticky lg:top-24 self-start w-full bg-surface border border-white/10 rounded-lg p-6" data-testid="checkout-summary">
            <h2 className="font-display text-lg">Resumo do pedido</h2>
            <div className="space-y-4 mt-5">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3 items-center" data-testid={`checkout-summary-item-${i.key.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                  <img src={i.product?.image_url} alt={i.product?.name} className="h-14 w-14 rounded object-cover bg-elevated" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{i.product?.name}</p>
                    <p className="text-xs text-mute">
                      {i.qty}x {formatBRL(i.product?.price)}{i.color ? ` • ${i.color}` : ""}
                    </p>
                  </div>
                  <p className="text-sm">{formatBRL((i.product?.price || 0) * i.qty)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 mt-5 pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-mute">Subtotal</span>
                <span>{formatBRL(total)}</span>
              </div>
              <div className="flex justify-between" data-testid="checkout-free-shipping">
                <span className="text-mute flex items-center gap-1"><Truck size={14} /> Frete</span>
                <span className="text-cyan font-medium">Grátis</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-mute">Total</span>
                <span className="font-display text-2xl text-cyan" data-testid="checkout-total">{formatBRL(total)}</span>
              </div>
            </div>
            <p className="text-xs text-mute flex items-start gap-2 mt-5">
              <ShieldCheck size={14} className="text-cyan shrink-0 mt-0.5" />
              Compra 100% segura. Seus dados são usados apenas para o envio do pedido.
            </p>
            <Link to="/loja" className="text-xs text-mute hover:text-cyan underline underline-offset-4 block mt-4 transition-colors" data-testid="checkout-back-shop">
              Continuar comprando
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
}
